import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Search, Filter, Clock } from 'lucide-react';
import { messageService } from '../../services/messageService';
import Header from '../../components/ui/Header';

export default function AgentMessagesV2Inbox() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    filterConversations();
  }, [searchTerm, conversations]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await messageService?.getAgentConversations();
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err?.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const filterConversations = () => {
    if (!searchTerm?.trim()) {
      setFilteredConversations(conversations);
      return;
    }

    const term = searchTerm?.toLowerCase();
    const filtered = conversations?.filter(conv => 
      conv?.enquiry?.destination?.toLowerCase()?.includes(term) ||
      conv?.enquiry?.tripType?.toLowerCase()?.includes(term) ||
      conv?.customer?.fullName?.toLowerCase()?.includes(term)
    );
    setFilteredConversations(filtered);
  };

  const handleConversationClick = (conversationId) => {
    navigate(`/agent/messages/${conversationId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No messages yet';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-blue-600" />
              Messages
            </h1>
            <p className="mt-2 text-gray-600">
              Manage conversations with your enquiry customers
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by destination, trip type, or customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <Filter className="w-5 h-5" />
                <span className="font-medium">Filters</span>
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="text-sm text-gray-600">Active Conversations</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {conversations?.filter(c => c?.status === 'open')?.length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="text-sm text-gray-600">Total Enquiries</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {conversations?.length}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="text-sm text-gray-600">Recent Activity</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {conversations?.filter(c => {
                  if (!c?.lastMessageAt) return false;
                  const diffHours = (new Date() - new Date(c.lastMessageAt)) / 3600000;
                  return diffHours < 24;
                })?.length}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading conversations...</p>
            </div>
          )}

          {/* Conversations List */}
          {!loading && filteredConversations?.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No conversations found' : 'No conversations yet'}
              </h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? 'Try adjusting your search terms' :'Conversations will appear here when customers contact you'}
              </p>
            </div>
          )}

          {!loading && filteredConversations?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {filteredConversations?.map((conversation) => (
                  <div
                    key={conversation?.id}
                    onClick={() => handleConversationClick(conversation?.id)}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Destination (Title) */}
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {conversation?.enquiry?.destination || 'Unknown Destination'}
                          </h3>
                          {conversation?.status === 'open' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </div>

                        {/* Trip Type (Subtitle) */}
                        <p className="text-sm text-gray-600 mb-3">
                          {conversation?.enquiry?.tripType || 'No trip type specified'}
                        </p>

                        {/* Additional Details */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          {conversation?.enquiry?.adults > 0 && (
                            <span>
                              {conversation?.enquiry?.adults} Adult{conversation?.enquiry?.adults > 1 ? 's' : ''}
                            </span>
                          )}
                          {conversation?.enquiry?.children > 0 && (
                            <span>
                              {conversation?.enquiry?.children} Child{conversation?.enquiry?.children > 1 ? 'ren' : ''}
                            </span>
                          )}
                          {conversation?.customer?.fullName && (
                            <span className="font-medium text-gray-900">
                              {conversation?.customer?.fullName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Last Message Time */}
                      <div className="flex items-center gap-2 text-sm text-gray-500 ml-4">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(conversation?.lastMessageAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}